<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-8 lg:p-10 font-sans text-slate-900 selection:bg-indigo-100">
    
    <header class="flex  md:flex-row justify-between items-start md:items-center mb-10 gap-6 animate-fade-in-down">
      <div class="space-y-1">
        <h1 class="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
          Portefeuille Clients
        </h1>
        <p class="text-slate-500 font-medium flex items-center gap-2">
          <span class="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
          Gestion des licences et supervision des infrastructures déployées.
        </p>
      </div>
      
      <button 
        @click="showAddModal = true"
        class="flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 shadow-lg shadow-slate-200 hover:-translate-y-0.5 transition-all active:scale-95"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M12 4v16m8-8H4" /></svg>
        Nouvelle Agence
      </button>
    </header>

    <div class="bg-white p-4 rounded-[2rem] shadow-sm border border-slate-200/60 mb-8 flex flex-wrap gap-4 items-center animate-fade-in-down" style="animation-delay: 100ms;">
      <div class="flex-1 min-w-[300px] relative group">
        <span class="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Rechercher un client par nom, SIRET ou email..." 
          class="w-full pl-14 pr-6 py-3.5 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-100 focus:bg-white transition-all text-sm font-bold placeholder:text-slate-400"
        >
      </div>
      <button 
        v-if="searchQuery" 
        @click="searchQuery = ''" 
        class="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all"
      >
        Réinitialiser
      </button>
    </div>

    <div class="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60 animate-fade-in-down" style="animation-delay: 200ms;">
      
      <div v-if="loading" class="p-8 space-y-6">
        <div v-for="n in 5" :key="n" class="flex items-center justify-between animate-pulse">
           <div class="flex gap-4 w-1/3 items-center">
             <div class="h-12 w-12 bg-slate-100 rounded-2xl"></div>
             <div class="space-y-2 flex-1">
               <div class="h-4 bg-slate-100 rounded w-3/4"></div>
               <div class="h-3 bg-slate-100 rounded w-1/2"></div>
             </div>
           </div>
           <div class="h-4 bg-slate-100 rounded w-24"></div>
           <div class="h-8 bg-slate-100 rounded-full w-32"></div>
           <div class="h-10 bg-slate-100 rounded-xl w-16"></div>
        </div>
      </div>

      <template v-else-if="clients.length === 0">
        <div class="p-16 text-center flex flex-col items-center justify-center">
          <div class="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
            <svg class="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
          <h3 class="text-xl font-black text-slate-900 mb-2">Aucune agence trouvée</h3>
          <p class="text-slate-400 font-medium">Commencez par déployer votre première infrastructure client.</p>
        </div>
      </template>

      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50/50 text-left text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 border-b border-slate-100">
                <th class="px-8 py-6">Client / Email</th>
                <th class="px-6 py-6 text-center">Bornes APK</th>
                <th class="px-6 py-6 text-center">Écrans TV</th>
                <th class="px-6 py-6 text-center">Statut Système</th>
                <th class="px-8 py-6 text-right">Configuration</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr v-for="client in paginatedClients" :key="client.id" class="hover:bg-slate-50/80 transition-colors group">
                <td class="px-8 py-6">
                  <div class="flex items-center gap-4">
                    <div class="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-md shadow-slate-200 uppercase group-hover:scale-105 transition-transform">
                      {{ client.nom.charAt(0) }}
                    </div>
                    <div>
                      <p class="font-black text-slate-900 group-hover:text-indigo-600 transition-colors text-base">{{ client.nom }}</p>
                      <p class="text-xs text-slate-400 font-medium tracking-wider">{{ client.siret }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6">
                  <div class="flex flex-col items-center gap-2">
                    <span class="text-xs font-black text-slate-700 tabular-nums">{{ client.bornesActives }} / {{ client.bornesTotal }}</span>
                    <div class="w-24 h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                      <div class="h-full bg-indigo-500 rounded-full transition-all duration-500" :style="{ width: (client.bornesTotal > 0 ? (client.bornesActives / client.bornesTotal * 100) : 0) + '%' }"></div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6 text-center">
                  <span class="px-4 py-2 bg-purple-50 text-purple-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-purple-100 shadow-sm">
                    {{ client.ecrans }} Devices
                  </span>
                </td>
                <td class="px-6 py-6 text-center">
                  <div :class="statusClasses(client.status)" class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider border shadow-sm">
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                    {{ client.statusLabel }}
                  </div>
                </td>
                <td class="px-8 py-6 text-right">
                  <button @click="openConfig(client)" class="p-3 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-100 hover:bg-indigo-50 rounded-xl transition-all shadow-sm group/btn opacity-80 group-hover:opacity-100" title="Paramétrer l'agence">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <TablePagination
          v-if="filteredClients.length > 0"
          :current-page="currentPage"
          @update:current-page="currentPage = $event"
          :total-items="filteredClients.length"
          :items-per-page="itemsPerPage"
          @update:items-per-page="itemsPerPage = $event; currentPage = 1"
          item-label="clients"
          class="px-8 py-6 border-t border-slate-100"
        />
      </template>
    </div>

    <Transition name="fade">
      <div v-if="showAddModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div class="bg-white w-full max-w-xl rounded-[2.5rem] p-10 shadow-2xl border border-white/20 animate-scale-in">
          <div class="flex justify-between items-center mb-8">
             <h2 class="text-2xl font-black text-slate-900 tracking-tight">Nouvelle Agence</h2>
             <button @click="showAddModal = false" class="text-slate-400 hover:text-slate-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          
          <p class="text-slate-500 mb-8 text-sm font-medium">Configurez les informations légales pour démarrer l'infrastructure.</p>
          
          <div class="space-y-5">
            <div class="space-y-2">
               <label class="text-[10px] font-black uppercase text-slate-400 ml-1 tracking-widest">Raison Sociale</label>
               <input v-model="newClient.nom" type="text" placeholder="Ex: Agence Paris Est" class="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800">
            </div>
            <div class="space-y-2">
               <label class="text-[10px] font-black uppercase text-slate-400 ml-1 tracking-widest">Email Contact</label>
               <input v-model="newClient.email" type="email" placeholder="contact@client.com" class="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800">
            </div>
            <div class="grid grid-cols-2 gap-5">
              <div class="space-y-2">
                <label class="text-[10px] font-black uppercase text-slate-400 ml-1 tracking-widest">Quota Bornes</label>
                <input v-model.number="newClient.bornesTotal" type="number" class="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800">
              </div>
              <div class="space-y-2">
                <label class="text-[10px] font-black uppercase text-slate-400 ml-1 tracking-widest">Quota Écrans</label>
                <input v-model.number="newClient.ecrans" type="number" class="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800">
              </div>
            </div>
          </div>

          <div class="flex gap-4 mt-10">
            <button @click="showAddModal = false" class="flex-1 py-4 rounded-2xl border-2 border-slate-100 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all">Annuler</button>
            <button @click="saveNewClient" class="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all transform active:scale-95">Créer le compte</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="slide-right">
      <div v-if="selectedClient" class="fixed inset-0 z-50 overflow-hidden">
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[4px]" @click="selectedClient = null"></div>
        <div class="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-[-20px_0_50px_rgba(0,0,0,0.1)] p-10 overflow-y-auto rounded-l-[3rem] border-l border-white/50">
          
          <div class="flex justify-between items-start mb-10 animate-fade-in-down">
            <div>
              <span class="text-[10px] font-black uppercase text-indigo-600 tracking-widest bg-indigo-50 px-3 py-1 rounded-lg">Configuration Master</span>
              <h2 class="text-3xl font-black text-slate-900 mt-2">{{ selectedClient.nom }}</h2>
              <p class="text-slate-400 text-xs font-mono mt-1">{{ selectedClient.siret }}</p>
            </div>
            <button @click="selectedClient = null" class="p-3 bg-slate-50 rounded-2xl hover:bg-slate-100 text-slate-400 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div class="space-y-10">
            <section class="bg-slate-50 p-8 rounded-[2.5rem] border border-slate-100 space-y-6">
              <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                Identité & Quotas
              </h3>
              
              <div class="space-y-4">
                <div>
                  <label class="block text-[9px] font-black text-slate-400 ml-1 mb-1 uppercase tracking-widest">Raison Sociale</label>
                  <input v-model="selectedClient.nom" type="text" class="w-full bg-white border-2 border-slate-200 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 font-bold text-slate-800 transition-all">
                </div>
                <div>
                  <label class="block text-[9px] font-black text-slate-400 ml-1 mb-1 uppercase tracking-widest">Email (Login)</label>
                  <input v-model="selectedClient.siret" type="text" class="w-full bg-white border-2 border-slate-200 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 font-bold text-slate-800 transition-all">
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-[9px] font-black text-slate-400 ml-1 mb-1 uppercase tracking-widest">Bornes Max</label>
                  <input v-model.number="selectedClient.bornesTotal" type="number" class="w-full bg-white border-2 border-slate-200 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 font-bold text-slate-800 transition-all">
                </div>
                <div>
                  <label class="block text-[9px] font-black text-slate-400 ml-1 mb-1 uppercase tracking-widest">Écrans Max</label>
                  <input v-model.number="selectedClient.ecrans" type="number" class="w-full bg-white border-2 border-slate-200 rounded-2xl py-3.5 px-5 focus:ring-0 focus:border-indigo-500 font-bold text-slate-800 transition-all">
                </div>
              </div>

              <div class="space-y-4 pt-4">
                <div class="flex items-center justify-between p-5 bg-white rounded-[1.5rem] border border-slate-200/60 shadow-sm">
                    <div>
                        <span class="text-xs font-black uppercase text-slate-700">Accès Système</span>
                        <p class="text-[10px] text-slate-400 mt-0.5 font-medium">Autoriser la connexion aux serveurs</p>
                    </div>
                    <button @click="toggleStatus(selectedClient)"
                        :class="selectedClient.status === 'active' ? 'bg-emerald-500' : 'bg-slate-200'"
                        class="w-14 h-8 relative rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span :class="selectedClient.status === 'active' ? 'translate-x-7' : 'translate-x-1'" class="absolute top-1 left-0 w-6 h-6 bg-white rounded-full shadow-sm transition-transform"></span>
                    </button>
                </div>

                <div class="flex items-center justify-between p-5 bg-white rounded-[1.5rem] border border-slate-200/60 shadow-sm">
                    <div>
                        <span class="text-xs font-black uppercase text-slate-700">Écran de Langues</span>
                        <p class="text-[10px] text-slate-400 mt-0.5 font-medium">Afficher le choix de langue sur APK</p>
                    </div>
                    <button @click="toggleLanguageScreen(selectedClient)"
                        :class="selectedClient.showLanguageScreen !== false ? 'bg-indigo-600' : 'bg-slate-200'"
                        class="w-14 h-8 relative rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span :class="selectedClient.showLanguageScreen !== false ? 'translate-x-7' : 'translate-x-1'" class="absolute top-1 left-0 w-6 h-6 bg-white rounded-full shadow-sm transition-transform"></span>
                    </button>
                </div>
              </div>
            </section>


            <section class="space-y-6 animate-fade-in-down" style="animation-delay: 100ms;">
              <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 mb-6">
                 <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>
                 Infrastructure Déployée
              </h3>

                <div class="mb-8">
                <h4 class="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-1">Écrans TV Connectés</h4>
                <div v-if="selectedClient.devices.filter(d => d.type === 'SCREEN').length === 0" class="p-4 border-2 border-dashed border-slate-200 rounded-2xl text-center text-xs text-slate-400 font-medium">Aucun écran configuré</div>
                <div
                  v-for="screen in selectedClient.devices.filter(d => d.type === 'SCREEN')"
                  :key="screen._id"
                  class="p-5 bg-white border-2 border-slate-100 rounded-[1.5rem] mb-4 group hover:border-purple-200 transition-all shadow-sm"
                >
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex items-center gap-4">
                      <div class="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </div>
                      <div class="flex-1">
                        <div v-if="editingDeviceId === screen._id" class="flex items-center gap-2 mb-2">
                          <input
                            v-model="editingDeviceName"
                            type="text"
                            @keyup.enter="saveDeviceName(screen)"
                            @keyup.esc="cancelEdit()"
                            class="flex-1 text-sm font-bold text-slate-900 bg-white border-2 border-indigo-300 rounded-lg px-2 py-1 focus:ring-0 focus:border-indigo-500"
                            autofocus
                          />
                          <button @click="saveDeviceName(screen)" class="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors" title="Sauvegarder">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                          </button>
                          <button @click="cancelEdit()" class="p-1.5 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors" title="Annuler">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <div v-else class="flex items-center gap-2">
                          <p class="text-sm font-black text-slate-900">{{ screen.name }}</p>
                          <button @click="startEditDeviceName(screen)" class="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 transition-all" title="Renommer">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                        </div>
                        <div v-if="screen.remoteAccess?.anydeskId" class="flex items-center gap-1.5 mt-0.5">
                          <span class="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></span>
                          <span class="text-[10px] font-mono text-purple-600 font-bold tracking-wider">
                            AnyDesk: {{ screen.remoteAccess.anydeskId }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button @click="copyLink(screen.connectionUrl)" class="bg-slate-900 text-white text-[9px] font-black px-4 py-2 rounded-xl uppercase tracking-widest hover:bg-purple-600 transition-colors shadow-sm">
                      Copier URL
                    </button>
                  </div>

                  <div class="flex items-center gap-3 mt-4 pt-4 border-t border-slate-50">
                    <div class="relative flex-1 group/input">
                      <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-purple-400 uppercase tracking-widest group-focus-within/input:text-purple-600 transition-colors">ID Remote</span>
                      <input
                        :value="screen.remoteAccess ? screen.remoteAccess.anydeskId : ''"
                        @input="if (!screen.remoteAccess) screen.remoteAccess = {}; screen.remoteAccess.anydeskId = $event.target.value"
                        type="text"
                        placeholder="Ex: 123 456 789"
                        class="w-full pl-24 pr-4 py-3 bg-slate-50 border-2 border-transparent rounded-xl text-xs font-mono font-bold focus:bg-white focus:border-purple-200 focus:ring-0 transition-all text-slate-800"
                      >
                    </div>

                    <button
                      @click="copyLink(screen.remoteAccess?.anydeskId)"
                      v-if="screen.remoteAccess?.anydeskId"
                      class="p-3 bg-purple-50 text-purple-500 rounded-xl hover:bg-purple-100 hover:text-purple-700 transition-all border border-purple-100"
                      title="Copier l'ID"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                    </button>
                  </div>
                </div>
              </div>
              
            <div>
              <h4 class="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-1">Bornes Interactives</h4>
              <div v-if="selectedClient.devices.filter(d => d.type === 'KIOSK').length === 0" class="p-4 border-2 border-dashed border-slate-200 rounded-2xl text-center text-xs text-slate-400 font-medium">Aucune borne configurée</div>
              <div
                v-for="kiosk in selectedClient.devices.filter(d => d.type === 'KIOSK')"
                :key="kiosk._id"
                class="p-5 bg-white border-2 border-slate-100 rounded-[1.5rem] mb-4 group hover:border-blue-200 transition-all shadow-sm"
              >
                <div class="flex items-start justify-between mb-4">
                  <div class="flex items-center gap-4">
                     <div class="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                     </div>
                     <div class="flex-1">
                        <div v-if="editingDeviceId === kiosk._id" class="flex items-center gap-2 mb-2">
                          <input
                            v-model="editingDeviceName"
                            type="text"
                            @keyup.enter="saveDeviceName(kiosk)"
                            @keyup.esc="cancelEdit()"
                            class="flex-1 text-sm font-black text-slate-900 bg-white border-2 border-indigo-300 rounded-lg px-2 py-1 focus:ring-0 focus:border-indigo-500"
                            autofocus
                          />
                          <button @click="saveDeviceName(kiosk)" class="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors" title="Sauvegarder">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                          </button>
                          <button @click="cancelEdit()" class="p-1.5 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors" title="Annuler">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <div v-else class="flex items-center gap-2">
                          <p class="text-sm font-black text-slate-900">{{ kiosk.name }}</p>
                          <button @click="startEditDeviceName(kiosk)" class="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 transition-all" title="Renommer">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                        </div>
                        <div v-if="kiosk.remoteAccess?.anydeskId" class="flex items-center gap-1.5 mt-0.5">
                        <span class="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                        <span class="text-[10px] font-mono text-rose-600 font-bold tracking-wider">
                            AnyDesk: {{ kiosk.remoteAccess.anydeskId }}
                        </span>
                        </div>
                     </div>
                  </div>

                  <button @click="showKioskKey(kiosk)" class="bg-slate-900 text-white text-[9px] font-black px-4 py-2 rounded-xl uppercase tracking-widest hover:bg-indigo-600 transition-colors shadow-sm">
                    {{ openedKioskId === kiosk._id ? "Masquer Clé" : "Voir Token" }}
                  </button>
                </div>

                <div class="flex items-center gap-3 mt-4 pt-4 border-t border-slate-50">
                  <div class="relative flex-1 group/input">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-rose-400 uppercase tracking-widest group-focus-within/input:text-rose-600 transition-colors">ID Remote</span>
                    <input 
                      :value="kiosk.remoteAccess ? kiosk.remoteAccess.anydeskId : ''"
                      @input="if (!kiosk.remoteAccess) kiosk.remoteAccess = {}; kiosk.remoteAccess.anydeskId = $event.target.value"
                      type="text" 
                      placeholder="Ex: 123 456 789"
                      class="w-full pl-24 pr-4 py-3 bg-slate-50 border-2 border-transparent rounded-xl text-xs font-mono font-bold focus:bg-white focus:border-rose-200 focus:ring-0 transition-all text-slate-800"
                    >
                  </div>
                  
                  <button 
                    @click="copyLink(kiosk.remoteAccess?.anydeskId)" 
                    v-if="kiosk.remoteAccess?.anydeskId"
                    class="p-3 bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-100 hover:text-rose-700 transition-all border border-rose-100"
                    title="Copier l'ID"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                  </button>
                </div>

                <Transition name="fade">
                    <div v-if="openedKioskId === kiosk._id" class="mt-4 p-4 bg-slate-800 rounded-2xl border border-slate-700/50">
                    <p class="text-indigo-300 uppercase text-[9px] font-black mb-2 tracking-[0.2em]">Clé d'authentification API</p>
                    <code class="block font-mono text-xs text-white break-all bg-slate-900/50 p-3 rounded-xl select-all">{{ kiosk.connectionToken }}</code>
                    </div>
                </Transition>
              </div>
            </div>
            </section>

            <section class="space-y-6 animate-fade-in-down" style="animation-delay: 200ms;">
               <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 mb-6">
                 <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                 Files d’attente Actives
               </h3>
              <div v-if="selectedClient.queues.length === 0" class="p-4 border-2 border-dashed border-slate-200 rounded-2xl text-center text-xs text-slate-400 font-medium">Aucune file configurée</div>
              <div v-for="queue in selectedClient.queues" :key="queue._id" class="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm mb-3">
                <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 text-xs font-black">{{ queue.prefix }}</span>
                <span class="text-sm font-bold text-slate-900 flex-1">{{ queue.name }}</span>
                <span v-if="queue.active" class="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-emerald-100">Active</span>
                <span v-else class="px-3 py-1 bg-slate-50 text-slate-400 rounded-lg text-[9px] font-black uppercase tracking-widest border border-slate-100">Inactive</span>
              </div>
            </section>


          <div class="pt-8 border-t border-slate-100 flex flex-col gap-4 animate-fade-in-down" style="animation-delay: 300ms;">
            <button @click="updateClient(selectedClient)" class="w-full py-5 bg-indigo-600 text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-xs shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-1 transition-all active:scale-95">
              Enregistrer la Configuration
            </button>
            
            <div class="grid grid-cols-2 gap-4 mt-2">
                <button @click="resetTickets(selectedClient.id)" class="py-4 bg-white text-orange-600 rounded-[1.5rem] font-black uppercase tracking-widest text-[10px] hover:bg-orange-50 border-2 border-orange-100 transition-all flex items-center justify-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Reset Tickets
                </button>
                <button @click="deleteClient(selectedClient.id)" class="py-4 bg-white text-rose-600 rounded-[1.5rem] font-black uppercase tracking-widest text-[10px] hover:bg-rose-50 border-2 border-rose-100 transition-all flex items-center justify-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  Supprimer l'agence
                </button>
            </div>
          </div>

        </div>
      </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import TablePagination from '@/components/TablePagination.vue';

export default {
  components: {
    TablePagination
  },
  data() {
    return {
      loading: true,
      showAddModal: false,
      selectedClient: null,
      newClient: { nom: '', siret: '', bornesTotal: 1, ecrans: 1 },
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      clients: [],
      openedKioskId: null,
      editingDeviceId: null,
      editingDeviceName: ''
    };
  },
  computed: {
    filteredClients() {
      if (!this.searchQuery) return this.clients;
      const query = this.searchQuery.toLowerCase();
      return this.clients.filter(client =>
        client.nom.toLowerCase().includes(query) ||
        client.siret.toLowerCase().includes(query)
      );
    },
    paginatedClients() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredClients.slice(start, end);
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchAgencies();
  },
  methods: {
    showKioskKey(kiosk) {
        this.openedKioskId = this.openedKioskId === kiosk._id ? null : kiosk._id
    },

    async fetchAgencies() {
      this.loading = true;
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch("https://api.datafuse.fr/api/super-admin/agencies", {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Erreur API");

        const data = await res.json();
        
        this.clients = data.map(agency => ({
          id: agency._id,
          nom: agency.name,
          siret: agency.email,
          bornesTotal: agency.devices.filter(d => d.type === "KIOSK").length,
          bornesActives: agency.devices.filter(d => d.type === "KIOSK" && d.online).length,
          ecrans: agency.devices.filter(d => d.type === "SCREEN").length,
          devices: agency.devices,
          queues: agency.queues,
          status: agency.status.toLowerCase(),
          statusLabel: agency.status === "ACTIVE" ? "Actif" : "Inactif",
          showLanguageScreen: agency.showLanguageScreen !== undefined ? agency.showLanguageScreen : true
        }));

      } catch (err) {
        console.error(err);
        toast.error("Impossible de récupérer les agences");
      } finally {
        // Simulation d'un chargement fluide
        setTimeout(() => { this.loading = false; }, 500);
      }
    },

    toggleStatus(client) {
      client.status = client.status === 'active' ? 'suspended' : 'active';
      client.statusLabel = client.status === 'active' ? 'Actif' : 'Inactif';
    },

    toggleLanguageScreen(client) {
      client.showLanguageScreen = !client.showLanguageScreen;
    },

    statusClasses(status) {
      switch(status) {
        case 'active': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
        case 'suspended': return 'bg-rose-50 text-rose-600 border-rose-100';
        default: return 'bg-slate-50 text-slate-600 border-slate-100';
      }
    },

    openConfig(client) {
      // Deep copy pour éviter de modifier la liste principale avant sauvegarde
      this.selectedClient = JSON.parse(JSON.stringify(client)); 
      this.openedKioskId = null;
    },

    async saveNewClient() {
        if (!this.newClient.nom || !this.newClient.email) {
            toast.warn("Veuillez remplir les champs obligatoires");
            return;
        }
        try {
          const token = localStorage.getItem("superAdminToken");
          const body = {
            name: this.newClient.nom,
            email: this.newClient.email,
            kiosksCount: this.newClient.bornesTotal,
            screensCount: this.newClient.ecrans,
            status: "ACTIVE"
          };

          const res = await fetch("https://api.datafuse.fr/api/super-admin/agency", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(body)
          });

          if (!res.ok) throw new Error();

          toast.success("Agence créée avec succès !");
          this.showAddModal = false;
          this.newClient = { nom: '', email: '', bornesTotal: 1, ecrans: 1 };
          this.fetchAgencies(); // Rafraîchir la liste

        } catch (err) {
          toast.error("Erreur lors de la création de l'agence");
        }
      },


    async updateClient(client) {
      try {
        const token = localStorage.getItem("superAdminToken");
        const devicesData = client.devices.map(d => ({
          _id: d._id,
          anydeskId: d.remoteAccess?.anydeskId || "",
          name: d.name
        }));

        const body = {
          name: client.nom,
          email: client.siret,
          kiosksCount: client.bornesTotal,
          screensCount: client.ecrans,
          status: client.status.toUpperCase(),
          showLanguageScreen: client.showLanguageScreen,
          devices: devicesData
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${client.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) throw new Error();

        toast.success("Configuration master mise à jour !");
        this.selectedClient = null;
        this.fetchAgencies(); // Rafraîchir pour avoir les données sync

      } catch (err) {
        toast.error("Échec de la sauvegarde");
      }
    },

    startEditDeviceName(device) {
      this.editingDeviceId = device._id;
      this.editingDeviceName = device.name;
    },

    cancelEdit() {
      this.editingDeviceId = null;
      this.editingDeviceName = '';
    },

    async saveDeviceName(device) {
      if (!this.editingDeviceName || this.editingDeviceName.trim() === '') {
        toast.warn("Le nom ne peut pas être vide");
        return;
      }

      try {
        const token = localStorage.getItem("superAdminToken");

        // Mettre à jour uniquement ce device
        const body = {
          devices: [{
            _id: device._id,
            name: this.editingDeviceName.trim(),
            anydeskId: device.remoteAccess?.anydeskId || ""
          }]
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${this.selectedClient.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) throw new Error();

        // Mettre à jour localement
        device.name = this.editingDeviceName.trim();

        toast.success("Nom du device mis à jour !");
        this.cancelEdit();

      } catch (err) {
        toast.error("Erreur lors de la mise à jour du nom");
      }
    },

    async resetTickets(agencyId) {
      if (!confirm("⚠️ ACTION IRRÉVERSIBLE : Supprimer TOUS les tickets de cette agence ?")) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${agencyId}/reset-tickets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error();
        const data = await res.json();
        toast.success(`♻️ Reset terminé : ${data.deletedTickets} tickets supprimés.`);

      } catch (err) {
        toast.error("Erreur lors du reset des tickets");
      }
    },

    async deleteClient(clientId) {
      if (!confirm("‼️ SUPPRESSION DÉFINITIVE : Cette action est irréversible. Continuer ?")) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${clientId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error();

        toast.success("Agence supprimée du réseau.");
        this.selectedClient = null;
        this.clients = this.clients.filter(c => c.id !== clientId);

      } catch (err) {
        toast.error("Impossible de supprimer l'agence");
      }
    },

    async copyLink(text) {
      try {
        await navigator.clipboard.writeText(text);
        toast.info("Copié dans le presse-papier !");
      } catch (err) {
        toast.error("Erreur de copie");
      }
    }
  }
}
</script>

<style scoped>
/* Animations Custom */
@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-down {
  animation: fadeInDown 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.animate-scale-in {
  animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-right-enter-active, .slide-right-leave-active {
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease;
}
.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Focus rings personnalisés pour les toggles */
button:focus-visible {
    @apply ring-2 ring-offset-2 ring-indigo-500 outline-none;
}
</style>